import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAuth,
  SavedLocation,
  MAX_SAVED_LOCATIONS,
  MAX_LOCATION_NAME_LENGTH,
} from '../context/AuthContext';
import { profileApi } from '../services/api';
import SubscriptionCard from '../components/SubscriptionCard';
import AppPromotionCard from '../components/AppPromotionCard';

/** Returns an error message, or '' when the list is valid. */
function validateLocations(locations: SavedLocation[]): string {
  const names = new Set<string>();
  const zips = new Set<string>();
  for (const loc of locations) {
    const name = loc.name.trim();
    if (!name) return 'Give each saved location a name';
    if (!/^\d{5}$/.test(loc.zipcode)) return `"${name}" needs a 5-digit zipcode`;
    if (names.has(name.toLowerCase())) return `The name "${name}" is used more than once`;
    if (zips.has(loc.zipcode)) return `Zipcode ${loc.zipcode} is listed more than once`;
    names.add(name.toLowerCase());
    zips.add(loc.zipcode);
  }
  return '';
}

function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const isPlus = user?.tier === 'plus';
  const [nickname, setNickname] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [locations, setLocations] = useState<SavedLocation[]>([]);
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const loadLocations = (saved: SavedLocation[], defaultZip: string) => {
    // First visit for a Plus user: start the list with their current default zipcode.
    const list = saved.length > 0 ? saved : [{ zipcode: defaultZip, name: '' }];
    setLocations(list);
    setDefaultIndex(Math.max(0, list.findIndex((loc) => loc.zipcode === defaultZip)));
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await profileApi.getProfile();
      const data = response.data;
      setNickname(data.nickname || '');
      setZipcode(data.zipcode || '');
      loadLocations(data.locations || [], data.zipcode || '');
      updateUser({ locations: data.locations || [] });
    } catch (err) {
      setNickname(user?.nickname || '');
      setZipcode(user?.zipcode || '');
      loadLocations(user?.locations || [], user?.zipcode || '');
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = (index: number, changes: Partial<SavedLocation>) => {
    setLocations((prev) => prev.map((loc, i) => (i === index ? { ...loc, ...changes } : loc)));
  };

  const addLocation = () => {
    if (locations.length < MAX_SAVED_LOCATIONS) {
      setLocations((prev) => [...prev, { zipcode: '', name: '' }]);
    }
  };

  const removeLocation = (index: number) => {
    if (locations.length <= 1) return;
    setLocations((prev) => prev.filter((_, i) => i !== index));
    setDefaultIndex((prev) => (index === prev ? 0 : index < prev ? prev - 1 : prev));
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (isPlus) {
      const cleaned = locations.map((loc) => ({ zipcode: loc.zipcode, name: loc.name.trim() }));
      const validationError = validateLocations(cleaned);
      if (validationError) {
        setError(validationError);
        return;
      }
      const defaultZip = cleaned[defaultIndex]?.zipcode || cleaned[0].zipcode;

      setSaving(true);
      try {
        await profileApi.updateProfile({ nickname, zipcode: defaultZip, locations: cleaned });
        updateUser({ nickname, zipcode: defaultZip, locations: cleaned });
        setLocations(cleaned);
        setZipcode(defaultZip);
        setMessage('Profile saved successfully');
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to save profile');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (zipcode && !/^\d{5}$/.test(zipcode)) {
      setError('Zipcode must be a 5-digit US zipcode');
      return;
    }

    setSaving(true);
    try {
      await profileApi.updateProfile({ nickname, zipcode });
      updateUser({ nickname, zipcode });
      setMessage('Profile saved successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? Your nickname, zipcodes and account will be permanently deleted. Any Google Play subscription will stop renewing; deleting the account ends access immediately and does not issue a refund. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      await profileApi.deleteAccount();
      logout();
      navigate('/');
    } catch (err: any) {
      alert('Failed to delete account. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-card">
        <h2>Profile</h2>
        <p className="profile-email">{user?.email}</p>
        <span className="tier-badge tier-current">{user?.tier}</span>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSaveProfile} className="profile-form">
          <div className="form-group">
            <label htmlFor="nickname">Nickname</label>
            <input
              id="nickname"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="What should we call you?"
            />
          </div>

          {isPlus ? (
            <fieldset className="form-group saved-locations">
              <legend>Saved Locations</legend>
              <span className="form-hint">
                U.S. ZIP codes only. Up to {MAX_SAVED_LOCATIONS} named locations. Switch between them from the dashboard. The
                default is shown when you open the app.
              </span>
              {locations.map((loc, i) => (
                <div className="saved-location-row" key={i}>
                  <input
                    type="text"
                    className="saved-location-name"
                    value={loc.name}
                    onChange={(e) => updateLocation(i, { name: e.target.value })}
                    placeholder={i === 0 ? 'e.g., Home' : 'e.g., Hunting Camp'}
                    maxLength={MAX_LOCATION_NAME_LENGTH}
                    aria-label={`Location ${i + 1} name`}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    className="saved-location-zip"
                    value={loc.zipcode}
                    onChange={(e) =>
                      updateLocation(i, { zipcode: e.target.value.replace(/\D/g, '').slice(0, 5) })
                    }
                    placeholder="Zip"
                    maxLength={5}
                    aria-label={`Location ${i + 1} zipcode`}
                  />
                  <label className="saved-location-default">
                    <input
                      type="radio"
                      name="defaultLocation"
                      checked={i === defaultIndex}
                      onChange={() => setDefaultIndex(i)}
                      aria-label={`Make location ${i + 1} the default`}
                    />
                    <span aria-hidden="true">Default</span>
                  </label>
                  <button
                    type="button"
                    className="saved-location-remove"
                    onClick={() => removeLocation(i)}
                    disabled={locations.length <= 1}
                    aria-label={`Remove location ${i + 1}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {locations.length < MAX_SAVED_LOCATIONS && (
                <button type="button" className="btn btn-secondary-dark btn-sm saved-location-add" onClick={addLocation}>
                  + Add location
                </button>
              )}
            </fieldset>
          ) : (
            <div className="form-group">
              <label htmlFor="zipcode">Default Hunting Zipcode</label>
              <input
                id="zipcode"
                type="text"
                inputMode="numeric"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="e.g., 21795"
                maxLength={5}
              />
              <span className="form-hint">
                U.S. ZIP codes only. Used for all forecast pages by default. Plus members can save up to
                {' '}{MAX_SAVED_LOCATIONS} named locations.
              </span>
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      <SubscriptionCard />

      <AppPromotionCard />

      <div className="profile-card profile-card-danger">
        <h3>Delete Account</h3>
        <p className="form-hint">
          Permanently delete your account and profile. Google Play subscriptions stop renewing.
          Deleting your account ends access immediately and does not issue a refund. This action cannot be undone.
        </p>
        <button
          className="btn btn-danger btn-full"
          onClick={handleDeleteAccount}
        >
          Delete My Account
        </button>
      </div>
    </div>
  );
}

export default ProfilePage;
